import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Eye } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../utils/api';
import DataTable from '../../components/ui/DataTable';
import StatusChip from '../../components/ui/StatusChip';
import { useAuth } from '../../context/AuthContext';

const DispatchList = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = statusFilter ? { status: statusFilter } : {};
    api.get('/dispatch', { params })
      .then((r) => setDispatches(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const columns = [
    {
      label: 'Dispatch #', key: 'dispatchNumber',
      render: (r) => <span className="font-mono text-zip-red text-xs font-medium">{r.dispatchNumber}</span>,
    },
    {
      label: 'Client', key: 'client',
      render: (r) => (
        <div className="min-w-0">
          <div className="text-zip-text truncate">{r.client?.name ?? '—'}</div>
          {r.consignee && <div className="text-[11px] text-zip-muted truncate">→ {r.consignee}</div>}
        </div>
      ),
    },
    {
      label: 'Items', key: 'items',
      render: (r) => <span className="font-mono text-zip-muted">{r.items?.length ?? 0}</span>,
    },
    {
      label: 'QC Status', key: 'qcStatus',
      render: (r) => <StatusChip status={r.qcStatus} />,
    },
    {
      label: 'Status', key: 'status',
      render: (r) => <StatusChip status={r.status} />,
    },
    { label: 'Dispatched By', key: 'dispatchBy', render: (r) => r.dispatchBy?.name ?? '—' },
    {
      label: 'Date', key: 'createdAt',
      render: (r) => (
        <span className="font-mono text-zip-muted text-xs">
          {format(new Date(r.createdAt), 'dd MMM yyyy')}
        </span>
      ),
    },
    {
      label: '', key: 'actions',
      render: (r) => (
        <Link
          to={`/dispatch/${r._id}`}
          className="inline-flex items-center gap-1.5 text-xs text-zip-muted hover:text-zip-red transition-colors font-mono"
        >
          <Eye size={13} /> View
        </Link>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex gap-3 justify-between">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="zip-input w-44"
        >
          <option value="">All Statuses</option>
          <option value="initiated">Initiated — Pending Dispatch QC</option>
          <option value="qc_pending">QC Pending</option>
          <option value="repacking">Repacking</option>
          <option value="inspection_failed">QC Inspection Failed</option>
          <option value="dispatched">Dispatched — Pending Security Outward</option>
          <option value="security_out">Security Outward — Pending Resource Security</option>
          <option value="resource_verified">Resource Verified — Pending Final Release</option>
          <option value="on_hold">On Hold at Final Security</option>
          <option value="released">Released</option>
        </select>
        {can('dispatch.create') && (
          <button
            onClick={() => navigate('/dispatch/new')}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={15} /> New Dispatch
          </button>
        )}
      </div>

      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-10 skeleton rounded" />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={dispatches}
          emptyText="No dispatch orders found."
        />
      )}
    </div>
  );
};

export default DispatchList;
